import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Message,
  useMarkRead,
  useMessages,
  useSendMessage,
  useVoiceUploadIntent,
  uploadVoiceBlob,
} from "../messagesApi";
import { LiveWorkoutChip } from "../components/today/LiveWorkoutChip";
import { EmptyState } from "../components/EmptyState";
import { VoiceRecorder, VoicePlayer } from "../components/voice";

function fmtTime(iso: string, locale: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/**
 * P1 ScreenChat canon — date dividers between message clusters.
 *
 * Today/Yesterday → localized one-word labels.
 * Older → «ВТОРНИК · 17 ИЮНЯ» pattern (weekday · day month) so the
 * athlete can scan back through chat history without guessing dates.
 */
export function fmtDateLabel(iso: string, locale: string, todayStr: string, yesterdayStr: string) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(yest.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return todayStr;
  if (sameDay(d, yest)) return yesterdayStr;
  const weekday = d.toLocaleDateString(locale, { weekday: "long" });
  const dayMonth = d.toLocaleDateString(locale, { day: "numeric", month: "long" });
  return `${weekday} · ${dayMonth}`;
}

function MessageBubble({ m, locale, voiceLabels }: { m: Message; locale: string; voiceLabels: VoiceUiLabels }) {
  const mine = m.sender_role === "athlete";
  // C6 — voice bubble. Renders VoicePlayer with the public R2 URL; no
  // text content typically present (the audio carries the meaning).
  if (m.content_type === "voice" && m.voice_url) {
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
        <div className="max-w-[85%] flex flex-col items-stretch gap-1">
          <VoicePlayer
            src={m.voice_url}
            durationSec={m.voice_duration_sec ?? null}
            transcript={m.transcript_text ?? null}
            lockedHint={
              !m.transcript_text && voiceLabels.transcriptLockedHint
                ? voiceLabels.transcriptLockedHint
                : null
            }
            labels={voiceLabels.player}
            side={mine ? "outgoing" : "incoming"}
            testId={`voice-message-${m.id}`}
          />
          <div
            className={`text-[10px] num-display flex items-center gap-1.5 ${
              mine ? "opacity-60 self-end" : "text-fg-muted self-start"
            }`}
          >
            <span>{fmtTime(m.created_at, locale)}</span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] px-3.5 py-2 text-sm whitespace-pre-line break-words ${
          mine
            ? "bg-accent text-accent-on rounded-card rounded-br-md"
            : "bg-bg-surface border border-bg-border text-fg-primary rounded-card rounded-bl-md"
        }`}
      >
        <div className="leading-relaxed">{m.content}</div>
        <div
          className={`text-[10px] mt-1 num-display flex items-center gap-1.5 ${
            mine ? "opacity-60" : "text-fg-muted"
          }`}
        >
          <span>{fmtTime(m.created_at, locale)}</span>
        </div>
      </div>
    </div>
  );
}

type VoiceUiLabels = {
  player: {
    play: string;
    pause: string;
    speed: string;
    transcript: string;
    transcriptShow: string;
    transcriptHide: string;
  };
  transcriptLockedHint: string | null;
};

type ChatPageProps = {
  /**
   * Render inside a side-sheet drawer (канон: чат — side-sheet в шапке) instead of as a
   * full route page. When true, drops the route-chrome height/margin
   * hacks and lets the container size the panel.
   */
  embedded?: boolean;
};

export function ChatPage({ embedded = false }: ChatPageProps = {}) {
  const { t, i18n } = useTranslation(["inbox", "common"]);
  const { data: msgs, isLoading, isError } = useMessages();
  const nav = useNavigate();
  const send = useSendMessage();
  const intent = useVoiceUploadIntent();
  const markRead = useMarkRead();
  const [text, setText] = useState("");
  const [voiceBusy, setVoiceBusy] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const voiceLabels: VoiceUiLabels = {
    player: {
      play: t("common:voice.play"),
      pause: t("common:voice.pause"),
      speed: t("common:voice.speed"),
      transcript: t("common:voice.transcript"),
      transcriptShow: t("common:voice.transcript_show"),
      transcriptHide: t("common:voice.transcript_hide"),
    },
    // Tier-gate is server-decided (transcript_text non-null means
    // generated). When null we show the upgrade hint to encourage
    // discovery without putting any tier-detection logic in the UI.
    transcriptLockedHint: t("common:voice.transcript_locked_hint"),
  };

  const handleVoiceComplete = async (
    blob: Blob,
    durationSec: number,
    mimeType: string,
  ) => {
    setVoiceBusy(true);
    try {
      const tsIso = new Date().toISOString();
      const presigned = await intent.mutateAsync({ ts_iso: tsIso, content_type: mimeType });
      await uploadVoiceBlob(presigned.upload_url, blob, mimeType);
      await send.mutateAsync({
        content: "",
        content_type: "voice",
        voice_url: presigned.public_url,
        voice_duration_sec: Math.max(1, Math.round(durationSec)),
      });
    } catch (e: unknown) {
      const status =
        (e as { response?: { status?: number } })?.response?.status ?? null;
      if (status === 503) {
        toast.error(t("common:voice.error_storage_offline"));
      } else {
        toast.error(t("common:voice.error_send_failed"));
      }
    } finally {
      setVoiceBusy(false);
    }
  };

  useEffect(() => {
    markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs?.length]);

  const onSend = () => {
    const v = text.trim();
    if (!v) return;
    send.mutate(v, { onSuccess: () => setText("") });
  };

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [text]);

  const todayStr = t("common:today");
  const yesterdayStr = t("common:yesterday");
  const locale = i18n.language;

  const groups: { label: string; items: Message[] }[] = [];
  for (const m of msgs || []) {
    const label = fmtDateLabel(m.created_at, locale, todayStr, yesterdayStr);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(m);
    } else {
      groups.push({ label, items: [m] });
    }
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] px-6 text-center gap-4">
        <div className="text-fg-primary font-semibold text-base">{t("chat_no_coach_title")}</div>
        <div className="text-fg-secondary text-sm leading-relaxed">
          {t("chat_no_coach_desc")}
        </div>
        <button
          onClick={() => nav("/marketplace")}
          className="mt-2 px-4 py-2 rounded-pill bg-accent hover:bg-accent-hover text-accent-on text-sm font-semibold transition-colors"
        >
          {t("chat_find_coach")}
        </button>
      </div>
    );
  }

  return (
    <div className={
      embedded
        ? "flex flex-col h-full"
        : "flex flex-col h-[calc(100vh-3.5rem-env(safe-area-inset-bottom))] -mb-24"
    }>
      {/* TG-2 in-progress workout HUD — null when no active workout */}
      <LiveWorkoutChip className="mx-3 mt-3 shrink-0" />

      {/* messages */}
      <div className="flex-1 overflow-y-auto px-3 pt-3 pb-2 space-y-3">
        {isLoading ? (
          <div className="text-fg-muted text-sm">{t("chat_loading")}</div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={ChatCircle}
            title={t("chat_empty_title")}
            description={t("chat_empty_desc")}
            testId="chat-empty"
          />
        ) : (
          groups.map((g, i) => (
            <div key={i} className="space-y-2">
              <div className="text-center text-[11px] text-fg-muted my-3 uppercase tracking-wider">
                {g.label}
              </div>
              {g.items.map((m) => (
                <MessageBubble key={m.id} m={m} locale={locale} voiceLabels={voiceLabels} />
              ))}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* input bar */}
      <div
        className="border-t border-bg-divider bg-bg-base/95 backdrop-blur supports-[backdrop-filter]:bg-bg-base/85 px-3 py-2.5 flex gap-2 items-end"
        style={{
          paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))",
          marginBottom: embedded ? "0" : "5rem",
        }}
      >
        <div
          className="flex-1 relative cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={t("chat_placeholder")}
            className="w-full resize-none rounded-pill bg-bg-surface border border-bg-border px-3.5 py-2.5 text-sm text-fg-primary placeholder:text-fg-dim focus:outline-none focus:border-accent focus:shadow-focus transition-shadow"
            style={{ caretColor: "var(--tw-prose-bullets)", minHeight: "40px" }}
          />
        </div>
        {/* Telegram-style send/mic toggle: text input non-empty → arrow
            send button; empty → voice recorder. Voice recorder
            internally swaps to its active-recording layout that takes
            the whole input bar over (positioned within the same flex
            row so layout doesn't jump). */}
        {text.trim() ? (
          <button
            onClick={onSend}
            disabled={send.isPending}
            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-pill bg-accent text-accent-on disabled:opacity-40 disabled:bg-bg-raised disabled:text-fg-muted transition-colors"
            aria-label={t("chat_send")}
            data-testid="chat-send"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        ) : (
          <VoiceRecorder
            onComplete={handleVoiceComplete}
            disabled={voiceBusy || send.isPending}
            micLabel={t("common:voice.mic_label")}
            hints={{
              slideToCancel: t("common:voice.slide_to_cancel"),
              slideToLock: t("common:voice.slide_to_lock"),
              locked: t("common:voice.locked"),
              permissionDenied: t("common:voice.permission_denied"),
              unsupported: t("common:voice.unsupported"),
            }}
            testId="chat-voice"
          />
        )}
      </div>
    </div>
  );
}
